import React, { useEffect, useState } from 'react'
import '../../style/detailed-view.scss'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Slide from '@mui/material/Slide'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import Fade from '@mui/material/Fade'
import { useAuth0 } from '../../contexts/auth0-context'
import endpoints from '../../API/endpoints'
import callAPI from '../../API/callSecureAPI'
import { useSelector } from 'react-redux'
import Chip from '@mui/material/Chip'
import SvgIcon from '@mui/material/SvgIcon'
import useStrategicImperativeDisplayName from '../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import Linking from '../Linking'
import Stack from '@mui/material/Stack'
import useInvalidateLinkingQueries from '../../shared/queries/linking/useInvalidateLinkingQueries'

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
})

export default function StrategicImperativeDetailedView({
    strategicImperative,
    forceParentRefresh,
    closePanel,
    editing,
    setEditing,
    triggerSave,
    triggerCancel,
    triggerDelete,
    navigationStack,
}) {
    const isDarkMode = useSelector((state) => (state?.userRecord ? state?.userRecord?.isDarkMode : true))

    const [isLoaded, setIsLoaded] = React.useState(false)
    const { isAuthenticated, getTokenSilently } = useAuth0()

    const [name, setName] = useState('')
    const [newName, setNewName] = useState('')

    const [forceRefresh, setForceRefresh] = useState(false)

    const [openDeleteModal, setOpenDeleteModal] = useState(false)

    const strategicImperativeDisplayName = useStrategicImperativeDisplayName()
    const { invalidateLinkingQueries } = useInvalidateLinkingQueries()

    useEffect(() => {
        if (forceRefresh) {
            setForceRefresh(false)
        }
        if (!isAuthenticated || !strategicImperative) {
            return
        }
        getSIData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [strategicImperative, forceRefresh, isAuthenticated])

    function updateState(si) {
        setName(si.name)
        setNewName(si.name)
    }

    async function saveSI() {
        setEditing(!editing)

        let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

        let newSI = {
            name: newName.replace(/\n+/gm, ' '),
            strategyId: strategicImperative.strategyId,
            order: strategicImperative.order,
            //organizationId: evidence.organizationId,
            linkedActivities: strategicImperative.linkedActivities,
        }
        await callAPI(
            endpoints.updateStrategicImperative.url + strategicImperative._id,
            endpoints.updateStrategicImperative.method,
            token,
            newSI
        )
        invalidateLinkingQueries()

        setName(newName)
        forceParentRefresh(true)
    }

    useEffect(() => {
        if (triggerSave === 0) {
            return
        }
        saveSI()
    }, [triggerSave])

    useEffect(() => {
        if (triggerCancel === 0) {
            return
        }
        handleCancel()
    }, [triggerCancel])

    useEffect(() => {
        if (triggerDelete === 0) {
            return
        }
        handleDeleteModalOpen()
    }, [triggerDelete])

    async function deleteThisSI() {
        let token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
        setOpenDeleteModal(false)
        await callAPI(
            endpoints.deleteStrategicImperative.url + strategicImperative._id,
            endpoints.deleteStrategicImperative.method,
            token
        )
        invalidateLinkingQueries()
        forceParentRefresh(true)
        closePanel()
    }

    async function getSIData() {
        updateState(strategicImperative)
        setIsLoaded(true)
    }

    function handleCancel() {
        setNewName(name)
        setEditing(false)
    }

    function handleDeleteModalClose() {
        setOpenDeleteModal(false)
    }

    function handleDeleteModalOpen() {
        setOpenDeleteModal(true)
    }

    function addToStack() {
        let arr = Array.from(navigationStack)
        arr.push({
            path: '/strategic-imperatives',
            data: strategicImperative,
        })
        return arr
    }

    return (
        <Fade in={isLoaded} timeout={800}>
            <Grid container item direction="column" sx={{ p: 0, backgroundColor: isDarkMode ? '#363943' : 'white' }}>
                <Dialog
                    open={openDeleteModal}
                    TransitionComponent={Transition}
                    keepMounted
                    onClose={handleDeleteModalClose}
                    aria-labelledby="alert-dialog-slide-title6"
                    aria-describedby="alert-dialog-slide-description6"
                >
                    <DialogTitle id="alert-dialog-slide-title6">
                        <Typography variant="h4" sx={{ pt: 1 }}>
                            {`Are you sure you want to delete this ${strategicImperativeDisplayName.singular}?`}
                        </Typography>
                    </DialogTitle>
                    <DialogActions>
                        <Button variant="outlined-sm-button" mb={1} onClick={handleDeleteModalClose}>
                            CANCEL
                        </Button>
                        <Button variant="contained-sm-button" sx={{ mb: 1, mr: 1 }} onClick={deleteThisSI}>
                            DELETE
                        </Button>
                    </DialogActions>
                </Dialog>

                {editing ? (
                    <Grid container direction="column">
                        <Grid item sx={{ ml: 2, mb: -2, mt: 2 }}>
                            <Typography variant="title2">
                                <strong>Title</strong>
                            </Typography>
                        </Grid>
                        <Grid item variant="rounded-top-grid" sx={{ m: 2 }}>
                            <TextField
                                id="edit-strategicimperative"
                                multiline
                                rows={2}
                                variant="filled"
                                placeholder={name}
                                value={newName}
                                onFocus={(event) => {
                                    event.target.setAttribute('autocomplete', 'off')
                                }}
                                onChange={(e) => setNewName(e.target.value)}
                                fullWidth
                                sx={{
                                    '& .MuiInputBase-root': {
                                        borderTopLeftRadius: '16px',
                                        borderTopRightRadius: '16px',
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container item>
                        <Grid
                            container
                            item
                            direction="column"
                            sx={{
                                p: 3,
                                borderBottomLeftRadius: '24px',
                                borderBottomRightRadius: '24px',
                                backgroundColor: isDarkMode ? '#5E6169' : '#F5F5F6',
                                boxShadow: 0,
                            }}
                        >
                            <Grid item>
                                <Typography variant="h5" sx={{ mb: 1 }}>
                                    {strategicImperativeDisplayName.singular}:
                                </Typography>
                            </Grid>
                            <Grid container item direction="row" spacing={1}>
                                <Grid item xs={10}>
                                    <div
                                        style={{
                                            display: 'inline-block',
                                            position: 'relative',
                                            zIndex: 2,
                                            userSelect: 'text',
                                            cursor: 'text',
                                            padding: '16px 20px',
                                            margin: '-16px -16px',
                                        }}
                                        onClick={(e) => e.stopPropagation()} // prevent collapse on text click
                                    >
                                        <Typography
                                            variant="h2"
                                            onDoubleClick={(e) => {
                                                const range = document.createRange()
                                                const selection = window.getSelection()
                                                range.selectNodeContents(e.target)
                                                selection.removeAllRanges()
                                                selection.addRange(range)
                                            }}
                                        >
                                            {name}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid // Adding an invisible grid with chip to maintain similar appearance as the VMs and Activities pages
                                    item
                                >
                                    <Chip
                                        sx={{ backgroundColor: isDarkMode ? '#5E6169' : '#F5F5F6' }}
                                        icon={<SvgIcon inheritViewBox fontSize="inherit" />}
                                        label={<Typography variant="chip"></Typography>}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Divider variant="middle" />

                        <Grid container item direction="column" spacing={0}>
                            <Stack direction="row" p={3}>
                                <Linking
                                    id={strategicImperative._id}
                                    type="strategicImperative"
                                    previousDataStack={addToStack()}
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Fade>
    )
}
